import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, 
  Mail, 
  Calendar,
  Clock,
  RefreshCw,
  Download,
  ArrowLeft,
  Lock,
  LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Senha de acesso à área administrativa
const ADMIN_PASSWORD = "mentoria2026";

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Verificar se já está autenticado (sessão)
  useEffect(() => {
    const auth = sessionStorage.getItem("admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("admin_auth", "true");
      setError("");
    } else {
      setError("Senha incorreta");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("admin_auth");
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      const sortedLeads = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setLeads(sortedLeads);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR");
  };

  const getExperienceText = (value) => {
    const experienceMap = {
      "menos-1-ano": "Menos de 1 ano",
      "1-3-anos": "1 a 3 anos",
      "3-5-anos": "3 a 5 anos",
      "5-10-anos": "5 a 10 anos",
      "mais-10-anos": "Mais de 10 anos",
    };
    return experienceMap[value] || value;
  };

  const exportToCSV = () => {
    const headers = ["Nome", "Email", "Idade", "Profissão", "Área", "Experiência", "Data"];
    const csvContent = [
      headers.join(","),
      ...leads.map(lead => [
        `"${lead.nome_completo}"`,
        lead.email,
        lead.idade,
        `"${lead.profissao}"`,
        `"${lead.area_atuacao}"`,
        `"${getExperienceText(lead.tempo_experiencia)}"`,
        `"${formatDate(lead.created_at)}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads_mentoria_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Tela de Login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="w-full max-w-md px-6">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-emerald-400" />
              </div>
              <h1 className="font-cabinet text-2xl font-bold mb-2">Área Restrita</h1>
              <p className="text-slate-400 text-sm">Digite a senha para acessar os leads</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a senha"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="admin-password"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"
                data-testid="admin-login-btn"
              >
                Acessar
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a href="/" className="text-slate-400 hover:text-emerald-400 text-sm">
                ← Voltar para a página inicial
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tela de Administração (autenticado)
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-slate-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </a>
            <h1 className="font-cabinet text-xl font-bold">
              Leads da Mentoria
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchLeads}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              onClick={exportToCSV}
              size="sm"
              className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total de Leads</p>
                <p className="text-2xl font-bold text-white">{leads.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Hoje</p>
                <p className="text-2xl font-bold text-white">
                  {leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Vagas Restantes</p>
                <p className="text-2xl font-bold text-white">{Math.max(0, 7 - leads.length)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">#</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Nome</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Email</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Idade</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Profissão</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Área</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Experiência</th>
                  <th className="text-left py-4 px-6 text-slate-400 font-medium text-sm">Data</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-400">
                      Carregando...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-400">
                      Nenhum lead cadastrado ainda.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead, index) => (
                    <tr key={lead.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-4 px-6 text-slate-500">{index + 1}</td>
                      <td className="py-4 px-6 text-white font-medium">{lead.nome_completo}</td>
                      <td className="py-4 px-6">
                        <a href={`mailto:${lead.email}`} className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {lead.email}
                        </a>
                      </td>
                      <td className="py-4 px-6 text-slate-300">{lead.idade}</td>
                      <td className="py-4 px-6 text-slate-300">{lead.profissao}</td>
                      <td className="py-4 px-6 text-slate-300">{lead.area_atuacao}</td>
                      <td className="py-4 px-6 text-slate-300">{getExperienceText(lead.tempo_experiencia)}</td>
                      <td className="py-4 px-6 text-slate-400 text-sm">{formatDate(lead.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
